import { Alert, Button, Input, message, notification, Skeleton, Space, Modal } from 'antd';
import Form, { FormInstance, FormItemProps } from 'antd/lib/form';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import APITokenUsersApiService from '../../api/APITokenUsersApiService';
import FeatureFlag from '../../consts/FeatureFlag';
import * as GetAPITokenUserDetailsHandler from '../../handlerModels/GetAPITokenUserDetailsHandler';
import * as SaveAPITokenUserDetailsHandler from '../../handlerModels/SaveAPITokenUserDetailsHandler';
import * as RegenerateAPITokenUserSecretHandler from '../../handlerModels/RegenerateAPITokenUserSecretHandler';
import APITokenUserDTO from '../../models/APITokenUserDTO';
import BaseFormProps from '../../redux/bases/BaseFormProps';
import BaseFormState from '../../redux/bases/BaseFormState';
import AuthorizationUtil from '../../utils/AuthorizationUtil';
import Guid from '../../utils/Guid';
import ValidationRuleUtil from '../../utils/ValidationRuleUtil';
import ValidationUtil from '../../utils/ValidationUtil';
import ResetButton from '../buttons/ResetButton';
import SaveButton from '../buttons/SaveButton';
import ValueLabel from '../general/ValueLabel';

interface APITokenUserDetailsFormState extends BaseFormState {
    apiTokenUser: APITokenUserDTO | null | undefined;
    regeneratedSecret: string | null;
}

interface APITokenUserDetailsFormProps extends BaseFormProps {
    apiTokenUserId: string;
  onSave?: (id: string) => void;
}

class APITokenUserDetailsForm extends React.Component<APITokenUserDetailsFormProps, APITokenUserDetailsFormState> {
  private readonly _formRef = React.createRef<FormInstance>();
  private _formItems = new Map<string, FormItemProps>()
    .set(APITokenUserDTO.apiName, {
      required: true,
      name: APITokenUserDTO.apiName,
      label: 'API Name',
      rules: [ValidationRuleUtil.required()]
    })
    .set(APITokenUserDTO.apiKey, {
      name: APITokenUserDTO.apiKey,
      label: 'API Key'
    })
    .set('Secret', {
        name: 'Secret',
        label: 'Secret'
    })

  constructor(props: APITokenUserDetailsFormProps) {
    super(props);

    this.state = {
        apiTokenUser: null,
        regeneratedSecret: null
    };
  }

  componentDidMount() {
    this.loadUser();
  }

  componentDidUpdate(prevProps: APITokenUserDetailsFormProps) {
    if (this.props.apiTokenUserId && this.props.apiTokenUserId != prevProps.apiTokenUserId) {
      this.loadUser();
    }
  }

  public resetForm = () => {
    this._formRef.current?.resetFields();
    this.setState({ altered: false })
  }

  private loadUser() {
    this.setState({ loading: true });

    const loaders = [];
    if (this.props.apiTokenUserId != Guid.Empty()) {
      loaders.push(this.loadExisting());
    }

    Promise.all(loaders).then(() => {
      this.setState({ loading: false });
    });
  }

  private loadExisting = () => {
    APITokenUsersApiService.getAPITokenUserDetails(this.props.apiTokenUserId)
      .then((results: GetAPITokenUserDetailsHandler.Result) => {
        if (results) {
          this.setState({
            apiTokenUser: results.apiTokenUser
          });

          this._formRef.current?.resetFields();
        }
      }).catch(() => {
        this.setState({ error: true });
      });
  }

  private handleSubmit = () => {
    this.setState({ submitting: true });

    const request = SaveAPITokenUserDetailsHandler.Request.create({
        APITokenUserId: this.props.apiTokenUserId,
        APITokenUser: APITokenUserDTO.create((this._formRef ? (this._formRef.current as any).getFieldsValue() : null))
    });

    APITokenUsersApiService.saveAPITokenUser(request)
      .then((result: SaveAPITokenUserDetailsHandler.Result) => {
        this.setState({
          submitted: true
        });

        if (result?.succeeded) {
          message.success('Saved');
          this.setState({
            apiTokenUser: result?.apiTokenUser,
            altered: !result.succeeded
          });

          if (result.apiTokenUser?.id) {
            if (this.props.onSave) {
              this.props.onSave(result.apiTokenUser.id);
            }
          }

          this._formRef.current?.resetFields();
        }
        else {
          this.setState({
            error: !result?.succeeded,
            message: result?.errors.join('\n'),
            fieldErrors: result?.fieldErrors
          });
          notification.error({ message: 'Uh oh!', description: result?.errors.join('\n'), });
        }
      })
      .catch((results: any) => {
        this.setState({ error: results });
        notification.error({ message: 'Uh oh!', description: 'User could not be saved.', });
      })
      .finally(() => {
        this.setState({ loading: false, submitting: false });
      });
  }

  private handleChange = () => {
    this.setState({ altered: true });
  }

  private regenerateSecret = (id: string) => {
    Modal.confirm({
      title: 'Are you sure you want to regenerate the secret?',
      okText: 'Regenerate',
      onOk: () => this.regenerateAPITokenUserSecret(id)
     });
  }

  private regenerateAPITokenUserSecret = (apiTokenUserId: string) => {
    const request = RegenerateAPITokenUserSecretHandler.Request.create({
        apiTokenUserId: apiTokenUserId,
    });

    APITokenUsersApiService.regenerateAPITokenUserSecret(request)
      .then((result: RegenerateAPITokenUserSecretHandler.Result | null) => {
        this.setState({
          error: !result?.succeeded,
          message: result?.errors.join('\n'),
          fieldErrors: result?.fieldErrors,
          altered: !result?.succeeded
        });

        if (result?.succeeded === true) {
            this.setState({ regeneratedSecret: result?.secret });
        }
        else {
          message.error(result?.errors.join('\n'));
        }
      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('The secret could not be regenerated.');
      })
      .finally(() => {
        this.setState({ loading: false });
      });
    }

  render() {
    if (this.state.loading || this.state.apiTokenUser == null || this.state.apiTokenUser == undefined) {
      return <Skeleton active={true} />;
    }
    const apiTokenUserId = this.state.apiTokenUser.id == null ? '' : this.state.apiTokenUser.id;

    return (
      <Space size="small" direction="vertical">
        {this.renderErrors()}
        <Form ref={this._formRef}
          layout="vertical"
          onChange={this.handleChange}
          onFinish={this.handleSubmit}
          requiredMark={true}
          initialValues={this.state.apiTokenUser}>

          <Form.Item
            {...ValidationUtil.getValidation(APITokenUserDTO.apiName, this.state.fieldErrors, this.state.submitted)}
            {...this._formItems.get(APITokenUserDTO.apiName)}>
            {AuthorizationUtil.isAuthorized([FeatureFlag.MANAGE_API_TOKEN_USER])
              ? <Input disabled={!this.props.isEditing || this.state.loading || this.state.submitting} />
              : <ValueLabel />}
          </Form.Item>

          <Form.Item
            {...ValidationUtil.getValidation(APITokenUserDTO.apiKey, this.state.fieldErrors, this.state.submitted)}
            {...this._formItems.get(APITokenUserDTO.apiKey)}>
            {<ValueLabel />}
          </Form.Item>

          <Form.Item
            {...this._formItems.get('Secret')}>
            {AuthorizationUtil.isAuthorized([FeatureFlag.MANAGE_API_TOKEN_USER]) && !this.state.regeneratedSecret
              ? <Button type="primary" disabled={!this.props.isEditing || this.state.loading || this.state.submitting} onClick={() => this.regenerateSecret(apiTokenUserId)}>Regenerate Secret</Button>
              : AuthorizationUtil.isAuthorized([FeatureFlag.MANAGE_API_TOKEN_USER]) && this.state.regeneratedSecret ? <> Please copy the secret below. It will not be shown again once you leave this page. <br /> <Text strong>{this.state.regeneratedSecret}</Text></>: 'No regenerate available'}
          </Form.Item>

          {this.renderActions()}
        </Form>
      </Space>
    );
  }

  renderActions() {
    if (this.props.isEditing && AuthorizationUtil.isAuthorized([FeatureFlag.MANAGE_API_TOKEN_USER])) {
      return (
        <Space direction='horizontal' wrap={true}>
          <SaveButton disabled={this.state.loading} saving={this.state.submitting} saved={this.state.saved} />
          <ResetButton disabled={!this.state.altered} resetting={this.state.resetting} onConfirm={this.resetForm} />
        </ Space>
      );
    }
  }

  renderErrors() {
    if (this.state.error) {
      return <Alert type="error" message='Error' showIcon={true} description='There were errors submitting your request. Please review the fields below.' />;
    }
  }
}

export default APITokenUserDetailsForm;
